<template>
  <scrollable-container>
    <placeholder v-show="isTableAwaiting" :text="text" @buttonClick="buildReport" />
    <loader v-show="isTableLoading" />
    <container
      v-show="isTableReady"
      id="printable"
      :class="[{[$style.message]: isTableAwaiting}, $style.scrollableContent]"
    >
      <table :class="$style.table">
        <thead :class="$style.header">
          <th v-for="column in columns" :key="column.name" :style="{width: column.width}" @click="changeSort(column)">
            <div :class="$style.tdInline">
              {{ column.name }}
            </div>
            <img
              src="@/assets/images/icons/table/sort-arrow.svg"
              :class="[
                $style.tdInline,
                $style.arrow,
                {
                  [$style.asc]: column.sort == 2,
                  [$style.hidden]: column.sort == 0,
                },
              ]"
            >
          </th>
        </thead>

        <tbody>
          <tr v-for="user in displayedTable" :key="user.name" :class="$style.item">
            <td
              v-for="column in columns"
              :key="column.name + user.name"
              :style="{width: column.width, backgroundColor: getColor(user[column.field], column.field)}"
            >
              {{ getField(user[column.field], column.field) }}
            </td>
          </tr>
        </tbody>
      </table>
    </container>
  </scrollable-container>
</template>

<script>
import ScrollableContainer from '@/components/common/blocks/scrollable-container.vue';
import Container from '@/components/common/blocks/container.vue';
import Placeholder from '@/components/common/blocks/report-placeholder.vue';
import Loader from '@/components/common/blocks/loader.vue';
import { getViolations } from '@/api/methods/reports/users/index';
import { convertSecondsToDays } from '@/helpers/format-date';
import { REPORT_ENUM } from '@/assets/scripts/reportEnum';

export default {
  components: {
    ScrollableContainer,
    Container,
    Placeholder,
    Loader,
  },
  data() {
    return {
      name: this.$t('navigation.reports.userWork.worktime.disciplineViolations.name'),
      tableData: [],
      text: this.$t('reports.pressButtonToGenerateReport'),
      columns: [
        { name: this.$t('reports.columns.employee'), sort: 2, field: 'user' },
        { name: this.$t('reports.columns.late'), sort: 0, field: 'late' },
        { name: this.$t('reports.columns.early'), sort: 0, field: 'early' },
        { name: this.$t('reports.columns.absent'), sort: 0, field: 'out' },
        { name: this.$t('reports.columns.underwork'), sort: 0, field: 'underwork' },
      ],
    };
  },
  computed: {
    isTableReady() {
      return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_READY;
    },
    isTableAwaiting() {
      return this.$store.getters['report/getStateTable'] === REPORT_ENUM.REQUEST_AWAITING;
    },
    isTableLoading() {
      return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_LOADING;
    },
    dateOption() {
      return this.$store.getters['filters/dateSelect'];
    },
    dateRange() {
      return this.$store.getters['filters/formattedDateRange'];
    },
    checkedComputers() {
      return this.$store.getters['pageSpecificData/checkedComputersList'];
    },
    computersTree() {
      return this.$store.getters['pageSpecificData/computersTree'];
    },
    isWorktimeOnly() {
      return this.$store.getters['filters/isWorktimeOnly'];
    },
    displayedTable() {
      return this.tableData.slice(0).sort(this.compare);
    },
  },
  watch: {
    dateRange(oldValue, newValue) {
      if (
        oldValue.dateFrom.toString() !== newValue.dateFrom.toString()
          || oldValue.dateTo.toString() !== newValue.dateTo.toString()
      ) {
        this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
      }
    },
    checkedComputers(oldArray, newArray) {
      if (oldArray !== newArray) {
        this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
      }
    },
    isWorktimeOnly(oldValue, newValue) {
      if (oldValue !== newValue) {
        this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
      }
    },
    dateOption(oldValue, newValue) {
      if (oldValue !== newValue) {
        this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
      }
    },
  },
  mounted() {
    this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
  },
  methods: {
    getColor(value, field) {
      if (field === 'underwork') {
        if (value > 0) return '#7cd890';
        return '#ffb0b0';
      }
      return '#FFFFFF';
    },
    getField(value, field) {
      if (field === 'underwork') return convertSecondsToDays(value);
      return value;
    },
    getCheckedComputersFromTree(node) {
      if (node.type === 'computer' && node.checked) this.computersArray.push(node);
      if (node.type === 'folder') {
        node.children.forEach((child) => this.getCheckedComputersFromTree(child));
      }
    },
    buildReport() {
      this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_LOADING);
      this.tableData = [];
      let workTimeOnly = 0;
      this.computersArray = [];

      if (this.computersTree) {
        this.computersTree.forEach((node) => this.getCheckedComputersFromTree(node));
      }

      if (this.dateRange.dateFrom && this.dateRange.dateTo && this.computersArray) {
        if (this.isWorktimeOnly) workTimeOnly = 1;

        this.promise = getViolations(
          this.computersArray,
          this.dateRange.dateFrom,
          this.dateRange.dateTo,
          workTimeOnly,
        ).then(({ data }) => {
          if (data && data.result) {
            this.tableData = data.result;
            this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_READY);

            const exportArray = [];
            // TODO: disciplineViolations настроить передачу данных

            this.$store.commit('exportData/setDisciplineViolations', { name: this.name, data: exportArray });
          } else {
            this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
          }
          return data;
        });
      }
    },
    compare(a, b) {
      const index = this.columns.findIndex((x) => x.sort > 0);
      const key = this.columns[index].field;

      a = a[key];
      b = b[key];

      if (typeof a === 'string' && typeof b === 'string') {
        a = a.toLowerCase();
        b = b.toLowerCase();
      }

      if (this.columns[index].sort === 2) {
        if (a > b) {
          return -1;
        }

        if (a < b) {
          return 1;
        }
        return 0;
      }

      if (this.columns[index].sort === 1) {
        if (a < b) {
          return -1;
        }

        if (a > b) {
          return 1;
        }

        return 0;
      }
      return 0;
    },
    changeSort(column) {
      const { sort } = column;

      this.columns.forEach((col) => {
        col.sort = 0;
      });

      if (sort === 1) {
        column.sort = 2;
      } else column.sort = 1;
    },
  },
};
</script>

<style lang="scss" module>
  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-track {
    background: white;
  }

  ::-webkit-scrollbar-thumb {
    background: $branchColor;
    border-radius: 2px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: darkgray;
  }

  .scrollableContent {
    margin: 0 8px 0 0;
    padding: 0 12px 0 20px;
    box-shadow: $shadow;
    flex: 1;
  }

  .message {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .table {
    width: 100%;
    margin-bottom: 15px;
    border-collapse: collapse;
    font-weight: 400;
    word-break: break-word;
    border: 1px solid $borderColor;
    padding-bottom: 20px;
  }

  .header {
    background: #f5f5f5;
    height: 31px;
    position: sticky;
    top: -2px;
    z-index: 3;

    th {
      border: 1px solid $borderColor;
      align-items: center;
      text-align: center;
      color: #232326;
      letter-spacing: -0.08px;
      font-size: 13px;
      padding: 7px 4px 4px 10px;
      cursor: pointer;
    }
  }

  .item {
    height: 29px;
    cursor: pointer;
    border-bottom: 0.2px solid #eee;

    &:hover {
      background: $light-gray;
    }

    td {
      border-right: 1px solid $borderColor;
      border-left: 1px solid $borderColor;
      align-items: center;
      letter-spacing: -0.24px;
      color: $black;
      font-weight: 300;
      font-size: 12px;
      line-height: 14px;
      text-align: center;

      font-size: 14px;
      letter-spacing: 0px;
      text-align: left;
      padding: 7px 4px 7px 10px;
    }
  }

  .tdInline {
    display: inline-flex;
  }

  .arrow {
    float: right;
    margin-top: 7px;
    margin-right: 5px;

    &.asc {
      transform: rotate(180deg);
    }

    &.hidden {
      visibility: hidden;
    }
  }
</style>
